/**
 * Adds the "Partnership", "Chat Now" and "Login / Register" actions to the header,
 * matching the reference layout.
 *
 * Done as progressive enhancement rather than by forking sections/header.liquid,
 * so Horizon updates stay mergeable. If the theme ever renames its header
 * columns this simply no-ops — the header still works, it just loses the two
 * buttons, which is a far better failure than a broken header.
 */
(() => {
  // snippets/tawk-chat.liquid opens live chat for links ending in #chat; without the widget this is the contact page.
  const CHAT_HREF = '/pages/contact#chat';
  const PARTNER_HREF = '/pages/partnership-program';

  const icon = (paths, filled) =>
    `<svg viewBox="0 0 24 24" fill="${filled ? 'currentColor' : 'none'}" stroke="currentColor" ` +
    `stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;

  function build() {
    const col = document.querySelector('.header__column--right');
    if (!col || col.querySelector('.mcp-header-actions')) return;

    const wrap = document.createElement('div');
    wrap.className = 'mcp-header-actions';

    // B2B enquiries are worth as much as a retail order, so the route to them sits in the header.
    // Outlined, to leave the gold and navy buttons as the loudest things in the row.
    const partner = document.createElement('a');
    partner.className = 'mcp-btn mcp-btn--partner';
    partner.href = PARTNER_HREF;
    // The label is hidden on narrower desktops to keep the row off the search field, so the
    // accessible name has to live on the link itself rather than in the span.
    partner.setAttribute('aria-label', 'Partnership Program');
    partner.title = 'Partnership Program';
    partner.innerHTML =
      icon('<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5.5A2.5 2.5 0 0 1 10.5 3h3A2.5 2.5 0 0 1 16 5.5V7"/><path d="M3 12h18"/>') +
      '<span>Partnership</span>';

    const chat = document.createElement('a');
    chat.className = 'mcp-btn mcp-btn--chat';
    chat.href = CHAT_HREF;
    chat.innerHTML =
      icon('<path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.9-.9L3 21l1.9-4.6A8.4 8.4 0 0 1 4 11.5a8.4 8.4 0 0 1 8.5-8.4h.5a8.4 8.4 0 0 1 8 8.4Z"/>') +
      '<span>Chat Now</span>';

    const account = document.createElement('a');
    account.className = 'mcp-btn mcp-btn--account';
    // routes.account_url is injected below by the Liquid snippet
    account.href = (window.__mcpAccountUrl || '/account');
    // A plain link: /account sends signed-out visitors to Shopify's customer login and signed-in
    // customers to their account. Forwarding the click to Horizon's <shopify-account> control did
    // nothing, because that web component only reacts to clicks inside its own shadow DOM.
    account.innerHTML =
      icon('<path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"/><circle cx="9.5" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/>') +
      '<span>Login / Register</span>';

    wrap.append(partner, chat, account);
    // brand-search.js mounts an inline search in this column; the buttons go after it.
    const search = col.querySelector('.mcp-search');
    if (search) search.after(wrap);
    else col.prepend(wrap);
  }

  // Desktop drawers are non-modal, so a click on the scrim (a ::after on
  // <body>, which reports <body> as the target) closes whichever drawer is open.
  document.addEventListener('pointerdown', (e) => {
    if (e.target !== document.body) return;
    document.querySelectorAll('theme-drawer[open]').forEach((d) => {
      if (typeof d.close === 'function') d.close();
    });
  });

  // Horizon measures the menu once on load; if the web font arrives after that
  // the chips shrink but nothing re-measures, leaving a stale MORE. Ask for a
  // reflow once fonts are in (the element listens for a 'reflow' event).
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      document.querySelectorAll('.header-menu overflow-list').forEach((list) => {
        list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
      });
    });
  }

  // Phones: a sticky "Free Quote / Chat" bar that appears once the hero's quote
  // form has scrolled out of view (or after a screen of scrolling on other pages).
  function mobileCta() {
    if (document.querySelector('.mcp-mobile-cta') || document.querySelector('.product-information') || document.querySelector('.cart-page')) return;
    const bar = document.createElement('div');
    bar.className = 'mcp-mobile-cta';
    bar.innerHTML =
      `<a class="mcp-mobile-cta__quote" href="${window.__mcpQuoteUrl || '/pages/free-quote'}">` +
      icon('<path d="M13 2 4.5 13.5H11l-1 8.5 8.5-11.5H13l1-8.5Z"/>', true) + '<span>Get a Free Quote</span></a>' +
      `<a class="mcp-mobile-cta__chat" href="${CHAT_HREF}">` +
      icon('<path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.9-.9L3 21l1.9-4.6A8.4 8.4 0 0 1 4 11.5a8.4 8.4 0 0 1 8.5-8.4h.5a8.4 8.4 0 0 1 8 8.4Z"/>') + '<span>Chat Now</span></a>';
    document.body.appendChild(bar);
    const form = document.querySelector('.hq__card');
    const update = () => {
      let show;
      if (form) { const r = form.getBoundingClientRect(); show = r.bottom < 0 || r.top > innerHeight; }
      else show = scrollY > innerHeight * 0.8;
      bar.classList.toggle('is-visible', show);
    };
    addEventListener('scroll', update, { passive: true });
    addEventListener('resize', update);
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build, { once: true });
  } else {
    build();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mobileCta, { once: true });
  else mobileCta();
  // Header re-renders in the theme editor; rebuild when Shopify swaps a section.
  document.addEventListener('shopify:section:load', build);
})();

/* Mobile menu drawer: brand header, plus a footer with Chat, Login / Register and trust points.
   Progressive enhancement like the header buttons above: if Horizon renames the drawer, this no-ops. */
(() => {
  // snippets/tawk-chat.liquid opens live chat for links ending in #chat; without the widget this is the contact page.
  const CHAT_HREF = '/pages/contact#chat';
  const svg = (paths) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
  function enhance() {
    const drawer = document.querySelector('.menu-drawer');
    if (!drawer || drawer.querySelector('.mcp-drawer-head')) return;
    const head = document.createElement('div');
    head.className = 'mcp-drawer-head';
    const home = document.createElement('a');
    home.className = 'mcp-drawer-head__logo';
    home.href = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    home.setAttribute('aria-label', 'Home');
    const logo = document.querySelector('header-component img');
    if (logo) { const img = logo.cloneNode(true); img.removeAttribute('id'); img.loading = 'eager'; home.appendChild(img); }
    else home.textContent = 'MASTERS';
    head.appendChild(home);
    drawer.prepend(head);

    const foot = document.createElement('div');
    foot.className = 'mcp-drawer-foot';
    foot.innerHTML =
      '<div class="mcp-drawer-foot__row">' +
      `<a class="mcp-drawer-foot__btn is-chat" href="${CHAT_HREF}">${svg('<path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.9-.9L3 21l1.9-4.6A8.4 8.4 0 0 1 4 11.5a8.4 8.4 0 0 1 8.5-8.4h.5a8.4 8.4 0 0 1 8 8.4Z"/>')}<span>Chat Now</span></a>` +
      `<a class="mcp-drawer-foot__btn is-account" href="${window.__mcpAccountUrl || '/account'}">${svg('<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>')}<span>Login / Register</span></a>` +
      '</div>' +
      `<a class="mcp-drawer-foot__btn is-partner" href="/pages/partnership-program">${svg('<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5.5A2.5 2.5 0 0 1 10.5 3h3A2.5 2.5 0 0 1 16 5.5V7"/><path d="M3 12h18"/>')}<span>Partnership Program</span></a>` +
      '<p class="mcp-drawer-foot__trust"><span>Free mockup</span><span>No setup fees</span><span>10-piece minimum</span></p>';
    const nav = drawer.querySelector('.menu-drawer__navigation');
    if (nav) nav.after(foot); else drawer.appendChild(foot);
  }
  // Horizon can build or re-render the drawer after load (and when it opens), so a one-off run at
  // load is not enough: whenever a menu drawer exists without our header, add it. enhance() is
  // idempotent, so our own insertions just return early. setTimeout (not rAF) so it also runs in
  // background tabs.
  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    setTimeout(() => { queued = false; enhance(); }, 50);
  };
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', enhance, { once: true });
  else enhance();
  document.addEventListener('shopify:section:load', enhance);
})();

/* Cart page on phones: a sticky bar with the estimated total and Check out while the summary is off
   screen. The button presses the theme's own checkout button, so checkout behaves exactly as usual. */
(() => {
  let watched = null;
  function mount() {
    if (!document.querySelector('main .cart-page')) return;
    let bar = document.querySelector('.mcp-cart-sticky');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'mcp-cart-sticky';
      bar.innerHTML =
        '<span class="mcp-cart-sticky__total"><small data-mcp-label>Estimated total</small><b data-mcp-total></b></span>' +
        '<button type="button" class="mcp-cart-sticky__btn"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg><span data-mcp-checkout>Check out</span></button>';
      document.body.appendChild(bar);
      bar.querySelector('button').addEventListener('click', () => {
        const real = document.querySelector('main .cart__checkout-button');
        if (real && !real.disabled) real.click();
      });
    }
    const summary = document.querySelector('main .cart-summary');
    const real = document.querySelector('main .cart__checkout-button');
    const total = document.querySelector('main .cart-totals__total-value');
    const label = document.querySelector('main .cart-totals__total-label');
    if (total) bar.querySelector('[data-mcp-total]').textContent = (total.getAttribute('value') || total.textContent).trim();
    if (label) bar.querySelector('[data-mcp-label]').textContent = label.textContent.trim();
    if (real) bar.querySelector('[data-mcp-checkout]').textContent = real.textContent.trim();
    watched = summary && real ? summary : null;
    update();
  }
  // Scroll-based rather than IntersectionObserver: deterministic and works in every tab state.
  // Shown while the summary is fully below the screen or scrolled past it.
  function update() {
    const bar = document.querySelector('.mcp-cart-sticky');
    if (!bar) return;
    if (!watched || !watched.isConnected) { bar.classList.remove('is-visible'); return; }
    const r = watched.getBoundingClientRect();
    bar.classList.toggle('is-visible', r.top > window.innerHeight || r.bottom < 0);
  }
  addEventListener('scroll', update, { passive: true });
  addEventListener('resize', update);
  let queued = false;
  const schedule = () => { if (queued) return; queued = true; setTimeout(() => { queued = false; mount(); }, 60); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true }); else mount();
  const main = document.querySelector('main');
  if (main) new MutationObserver(schedule).observe(main, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['value'] });
})();

/* Utility links in the announcement bar. The catalogue's last few entries — blank caps,
   supplies, support — are not patch types, so on desktop they leave the nav's MORE and sit
   as a small row on the right of the navy bar. Same progressive-enhancement rule as the rest
   of this file: if Horizon renames the bar or the list, nothing happens and the nav keeps
   them. Phones are untouched: the drawer still lists everything, submenus included. */
(() => {
  const TITLES = ['Blank Caps', 'Supplies', 'Support'];
  const titleOf = (li) => {
    const link = li.querySelector('a.menu-list__link');
    if (!link) return '';
    const title = link.querySelector('.menu-list__link-title');
    return (title ? title.textContent : link.textContent).trim();
  };

  // Horizon re-renders the menu list after load, so marks on one list instance do not last:
  // mark whatever list exists right now, every time anything in the header changes. The CSS
  // hides marked items on desktop, so the overflow list measures them at zero width.
  function markAll() {
    document.querySelectorAll('.header-menu overflow-list').forEach((list) => {
      list.querySelectorAll(':scope > li').forEach((li) => {
        li.classList.toggle('mcp-moved', TITLES.includes(titleOf(li)));
      });
    });
  }

  function build() {
    markAll();
    if (document.querySelector('.mcp-utility')) return;
    const bar = document.querySelector('.announcement-bar');
    const list = document.querySelector('.header-menu overflow-list');
    if (!bar || !list) return;
    const picked = [];
    list.querySelectorAll(':scope > li').forEach((li) => {
      const text = titleOf(li);
      if (!TITLES.includes(text)) return;
      picked.push({ text, href: li.querySelector('a.menu-list__link').getAttribute('href') });
    });
    if (!picked.length) return;
    const nav = document.createElement('nav');
    nav.className = 'mcp-utility';
    nav.setAttribute('aria-label', 'More from the shop');
    picked.forEach((item) => {
      const a = document.createElement('a');
      a.href = item.href;
      a.textContent = item.text;
      nav.appendChild(a);
    });
    bar.appendChild(nav);
    // The overflow list measured itself with those items present; ask it to look again.
    dispatchEvent(new Event('resize'));
  }

  // Inset the bar to the header's content edge so the message lines up with the logo and the
  // links with the cart, whatever padding the header has at this width.
  function alignBar() {
    const bar = document.querySelector('.announcement-bar');
    const edge = document.querySelector('header-component .header__column--left') || document.querySelector('header-component img');
    if (!bar || !edge) return;
    const left = Math.round(edge.getBoundingClientRect().left);
    if (left > 0 && left < 200) bar.style.setProperty('--mcp-bar-inset', left + 'px');
  }

  const pass = () => { build(); alignBar(); };
  pass();
  document.addEventListener('DOMContentLoaded', pass);
  addEventListener('load', pass);
  addEventListener('resize', () => { markAll(); alignBar(); });
  const header = document.querySelector('header-component') || document.body;
  new MutationObserver(markAll).observe(header, { childList: true, subtree: true });
})();
